import { api } from "./api";
import type { BannerSeverity } from "./notificationBannerService";

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  phoneNumber: string | null;
  organizationName: string | null;
  subscriptionPaid: boolean;
  cameraInstalled: boolean;
  optimizedForOpenings: boolean;
  optimizedForWindrows: boolean;
  createdAt: string;
  deviceCount: number;
  /** Boundaries summed over all seasons. */
  fieldBoundaryCount: number;
};

export type AdminUserDetail = {
  user: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    phoneNumber: string | null;
    organizationName: string | null;
    subscriptionPaid: boolean;
    cameraInstalled: boolean;
    optimizedForOpenings: boolean;
    optimizedForWindrows: boolean;
    createdAt: string;
  };
  devices: Array<{
    attachmentId: string;
    esn: string;
    name: string;
    lastConnectedAt: string | null;
    createdAt: string;
    detachedAt: string | null;
  }>;
  fieldsCount: number;
  seasonsCount: number;
};

/**
 * One cached day from `device_health_daily_stats`, as the client devices screen
 * gets it (`THealthDay`) plus `workSeconds` — the activity-grid metric. Both the
 * grid and the sensor trend read this one array, so the admin list draws both
 * charts without a second request.
 */
export type AdminHealthDay = {
  day: string;
  /** Per-day indicator, not the alert verdict — shades the trend's fault bands. */
  severity: "ok" | "advisory" | "critical";
  /**
   * Mean |value| / |freq| over the day's working points; null when the day was
   * empty. `health.channel` decides which of the two the trend line plots.
   */
  avgValue: number | null;
  avgFreq: number | null;
  /** Working seconds — the activity grid's metric. */
  workSeconds: number | null;
};

export type AdminDevice = {
  esn: string;
  imei: string | null;
  iccid: string | null;
  alias: string | null;
  model: string | null;
  deviceType: number;
  sensorMode: number;
  bootloaderVersion: string | null;
  strategyVersion: string | null;
  moduleVersion: string | null;
  hardwareVersion: string | null;
  gsmFirmwareVersion: string | null;
  ggnsFirmwareVersion: string | null;
  accelerometerFirmwareVersion: string | null;
  width: number | null;
  distance: number | null;
  delay: number | null;
  createdAt: string;
  updatedAt: string;
  lastConnectedAt: string | null;
  lastPosition: [number, number] | null;
  attachedUser: { id: string; name: string } | null;
  attachmentName: string | null;
  /** Only present when getDevices was called with `healthDays`. */
  health?: {
    kind: "potato" | "grain" | null;
    /** Which mean the sparkline plots — `freq` for FREQUENCY-mode potato. */
    channel: "value" | "freq";
    days: AdminHealthDay[];
  };
};

export type AdminMessage = {
  id: number;
  topic: string | null;
  deviceEsn: string | null;
  messageType: number | null;
  sensorValuesCount: number | null;
  parseError: string | null;
  receivedAt: string;
  parsed: boolean;
};

export type AdminMessageDetail = {
  id: number;
  topic: string | null;
  deviceEsn: string | null;
  messageType: number | null;
  payload: string | null; // base64
  parsed: any;
  parseError: string | null;
  sensorValuesCount: number | null;
  receivedAt: string;
};

export type AdminField = {
  id: string;
  userId: string;
  userName: string;
  name: string;
  description: string | null;
  geometry: any;
  areaHectares: number | null;
  color: string | null;
  createdAt: string;
  deletedAt: string | null;
};

export type AdminSeason = {
  id: string;
  userId: string;
  name: string;
  start: string;
  end: string;
  isCurrent: boolean;
  createdAt: string;
};

export type AdminFarm = {
  id: string;
  userId: string;
  name: string;
  isDefault: boolean;
};

export type AdminAttachment = {
  id: string;
  userId: string;
  deviceEsn: string;
  name: string | null;
  description: string | null;
  reaperWidth: number | null;
  createdAt: string;
  detachedAt: string | null;
};

export type AdminDeviceDetail = {
  device: {
    esn: string;
    imei: string | null;
    iccid: string | null;
    alias: string | null;
    description: string | null;
    model: string | null;
    deviceType: number;
    sensorMode: number;
    sensorMin: number | null;
    sensorMax: number | null;
    width: number | null;
    distance: number | null;
    delay: number | null;
    minSatellitesCount: number | null;
    /** Potato conveyor / no GPS: backend skips speed gate in dashboard analytics. */
    isStaticDevice: boolean;
    /** A fuel level sensor is wired to the TCU analog input (fuel mV on tracks). */
    fuelSensor?: boolean;
    settingsRevision: number | null;
    moduleVersion: string | null;
    hardwareVersion: string | null;
    bootloaderVersion: string | null;
    strategyVersion: string | null;
    gsmFirmwareVersion: string | null;
    ggnsFirmwareVersion: string | null;
    accelerometerFirmwareVersion: string | null;
    /** "greengrowth" (IoT over MQTT) | "johndeere" (imported Deere combine, CAN math). */
    source?: string;
    /** Pre-calibration mass coefficient; Deere devices carry JD_FULL_SCALE_KG (100). */
    defaultCoefficient?: number | null;
    createdAt: string;
    updatedAt: string;
  };
  /** Present for source = "johndeere": the Operations Center machine behind the device. */
  johnDeere?: {
    orgId: string;
    equipmentId: string;
    principalId: string | null;
    vin: string | null;
    name: string | null;
    lastImportAt: string | null;
    lastError: string | null;
  } | null;
  status: {
    lastConnectedAt: string;
    lastLongitude: number | null;
    lastLatitude: number | null;
    lastPositionAt: string | null;
  } | null;
  attachments: Array<{
    id: string;
    userId: string;
    name: string | null;
    description: string | null;
    createdAt: string;
    detachedAt: string | null;
  }>;
};

export type AdminCalibration = {
  id: string;
  attachmentId: string;
  // Null for a driver-app calibration whose window matched none of the owner's fields.
  fieldId: string | null;
  start: string;
  stop: string;
  reaperWidthMeters: number | null;
  weightKilograms: number | null;
  revision: number | null;
  coefficient: number | null;
  coefficientUpdatedAt: string | null;
  deviceTimezoneSeconds: number | null;
  status: string | null;
  error: string | null;
  createdAt: string;
  // Driver-app calibrations (driver-access spec §7). Optional: an older backend omits them.
  source?: "web" | "mobile";
  recordedBy?: { id: string; name: string | null; email: string } | null;
  /** The phone's own coefficient — reference only, never used by analytics. */
  phoneCoefficient?: number | null;
};

export type AdminTrackPoint = {
  id: number;
  time: string;
  longitude: number;
  latitude: number;
  value: number | null;
  freq: number | null;
  speed: number;
  /** Normalized sensor 0–1 from device settings (same as client tracks `norm`) */
  norm?: number | null;
  /** Visible GPS satellites (from device) */
  satQty?: number | null;
  /** GPS HDOP when available */
  gpsHdop?: number | null;
  /** Potato-camera per-point counts (null/absent = point carried no camera data) */
  cvPotatoCount?: number | null;
  cvStoneCount?: number | null;
  /** 8-bucket size histogram from edge-cv, when present */
  cvSizeHist?: number[] | null;
  /** Fuel sensor input, mV (null unless the device has the fuel-sensor switch on) */
  fuelMv?: number | null;
};

export type AdminDeviceRequest = {
  id: number;
  esn: string;
  data: any;
  status: string | null;
  errorType: string | null;
  errorDescription: string | null;
  createdAt: string;
  updatedAt: string;
  canceledAt: string | null;
};

// We keep adminFetch signature somewhat similar but use our new api client underneath
// to standardize error handling and base url logic.
async function adminFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `/api/admin${path}`;
  const method = options?.method ?? 'GET';
  const headers = options?.headers;
  const body = options?.body ? JSON.parse(options.body as string) : undefined;

  // We are mapping the old fetch signature to the new api client signature
  // Note: the `path` argument here already contains query strings from the old callers.
  // The new `api` client handles `BASE_URL` automatically.

  if (method === 'GET') {
      return api.get<T>(url, undefined, { headers: headers as any });
  }
  if (method === 'POST') {
      return api.post<T>(url, body, { headers: headers as any });
  }
  if (method === 'PUT') {
      return api.put<T>(url, body, { headers: headers as any });
  }
  if (method === 'PATCH') {
      return api.patch<T>(url, body, { headers: headers as any });
  }
  if (method === 'DELETE') {
      return api.delete<T>(url, undefined, { headers: headers as any });
  }

  throw new Error(`Unsupported method ${method}`);
}

export const checkAdmin = (): Promise<{
  isAdmin: boolean;
  email: string;
  isSuperAdmin?: boolean;
}> => adminFetch("/me");

// ── API Keys (Super Admin only) ────────────────────────────────────

export type AdminApiKey = {
  id: string;
  name: string;
  prefix: string;
  ownerId: string;
  ownerName?: string;
  ownerEmail?: string;
  /**
   * Stored and echoed back by the backend, but read by NOTHING: every valid key
   * gets FULL_ACCESS (backend-ai src/middleware/auth.ts). Kept on the type only
   * because the endpoint still returns it for keys created before the admin UI's
   * scopes picker was removed. Do not surface it as a permission.
   */
  scopes?: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  /** Server-computed flags, so long-lived or stale keys are easy to spot. */
  neverExpires?: boolean;
  expired?: boolean;
  neverUsed?: boolean;
};

export const getApiKeys = (): Promise<{ keys: AdminApiKey[] }> =>
  adminFetch("/api-keys");

export const createApiKey = (body: {
  name: string;
  userId?: string;
  /** Lifetime in days; `null` (or omitted) creates a key that never expires. */
  expiresInDays?: number | null;
}): Promise<{ key: string; apiKey: AdminApiKey }> =>
  adminFetch("/api-keys", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const revokeApiKey = (id: string): Promise<{ ok: boolean }> =>
  adminFetch(`/api-keys/${id}`, {
    method: "DELETE",
  });

export const getUsers = (params?: {
  search?: string;
  verified?: "verified" | "unverified";
  subscription?: "paid" | "unpaid";
  devices?: "with" | "without";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}): Promise<{ users: AdminUser[]; total: number }> => {
  return api.get(`/api/admin/users`, params);
};

export const getUser = (id: string): Promise<AdminUserDetail> =>
  adminFetch(`/users/${id}`);

export const getDevices = (params?: {
  search?: string;
  status?: "online" | "offline";
  attached?: "with" | "without";
  sort?: "lastConnectedAt" | "createdAt";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  /** Attach `health.days` for each device in the page. Omit to skip the read. */
  healthDays?: number;
}): Promise<{ devices: AdminDevice[]; total: number }> => {
  return api.get(`/api/admin/devices`, params);
};

export const getDevice = (esn: string) =>
  adminFetch(`/devices/${esn}`);

export const getMessages = (params?: {
  deviceEsn?: string;
  messageType?: number;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<{ messages: AdminMessage[]; total: number }> => {
  return api.get(`/api/admin/messages`, params);
};

export const getMessage = (id: number): Promise<AdminMessageDetail> =>
  adminFetch(`/messages/${id}`);

export type AdminDeviceLog = {
  id: number;
  installId: string;
  /** Which app produced the log, e.g. "greengrowth" | "truck2truck". */
  source: string;
  deviceEsn: string | null;
  level: "debug" | "info" | "warn" | "error" | "fatal";
  tag: string;
  message: string;
  stacktrace: string | null;
  metadata: Record<string, unknown> | null;
  appVersion: string | null;
  androidVersion: string | null;
  deviceModel: string | null;
  fingerprint: string;
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
};

export const getDeviceLogs = (params?: {
  installId?: string;
  source?: string;
  deviceEsn?: string;
  level?: "debug" | "info" | "warn" | "error" | "fatal";
  tag?: string;
  search?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<{ logs: AdminDeviceLog[]; total: number }> => {
  return api.get(`/api/admin/device-logs`, params);
};

export const getSettingsSchema = (): Promise<{
  id?: number;
  version: string | null;
  schema: any;
  createdAt?: string;
  updatedAt?: string;
}> => adminFetch("/settings/schema");

export const updateSettingsSchema = (data: {
  version: string;
  schema: Record<string, unknown>;
}): Promise<{ ok: boolean }> =>
  adminFetch("/settings/schema", {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getUserSettings = (
  userId: string
): Promise<{
  userId: string;
  settings: any;
  schemaVersion: string | null;
  updatedAt?: string;
  /** True when the user has nothing stored and the API handed back schema defaults. */
  isDefault?: boolean;
}> => adminFetch(`/users/${userId}/settings`);

export const updateUserSettings = (
  userId: string,
  data: { settings: Record<string, unknown>; schemaVersion?: string }
): Promise<{ ok: boolean }> =>
  adminFetch(`/users/${userId}/settings`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const updateUserSubscription = (
  userId: string,
  paid: boolean
): Promise<{ ok: boolean; subscriptionPaid: boolean }> =>
  adminFetch(`/users/${userId}/subscription`, {
    method: "PUT",
    body: JSON.stringify({ paid }),
  });

export const updateUserCameraInstalled = (
  userId: string,
  installed: boolean
): Promise<{ ok: boolean; cameraInstalled: boolean }> =>
  adminFetch(`/users/${userId}/camera-installed`, {
    method: "PUT",
    body: JSON.stringify({ installed }),
  });

export interface EmailDelivery {
  resendId: string;
  email: string;
  subject: string;
  /** 'sent' | 'delivered' | 'bounced' | 'complained' | 'failed' | 'delivery_delayed' | ... */
  lastEvent: string;
  lastEventAt: string;
  failureReason: string | null;
  sentAt: string | null;
}

/**
 * What actually happened to the emails we sent to an address, as reported by
 * Resend's webhook. Empty for anything sent before the webhook was configured —
 * no history is backfilled.
 *
 * Note that "delivered" means the receiving server accepted the message;
 * Microsoft 365 tenants can still divert it into an admin quarantine the
 * recipient never sees, which produces no further event.
 */
export const getEmailDeliveries = (params?: {
  email?: string;
  problemsOnly?: boolean;
  limit?: number;
}): Promise<{ deliveries: EmailDelivery[] }> => {
  const q = new URLSearchParams();
  if (params?.email) q.set("email", params.email);
  if (params?.problemsOnly) q.set("problemsOnly", "true");
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return adminFetch(`/email-deliveries${qs ? `?${qs}` : ""}`);
};

/**
 * Manually confirm a user's email address, bypassing the OTP mail. Login is
 * gated on this flag server-side, so this unblocks accounts that never received
 * the verification code.
 */
export const updateUserEmailVerified = (
  userId: string,
  verified: boolean
): Promise<{ ok: boolean; emailVerified: boolean }> =>
  adminFetch(`/users/${userId}/email-verified`, {
    method: "PUT",
    body: JSON.stringify({ verified }),
  });

/**
 * Mark an account as harvesting with field openings. Backend-side this is a
 * pure data flag — the external analytics scripts read it to decide whether to
 * run the opening-aware yield-map optimization for this user.
 */
export const updateUserOptimizedForOpenings = (
  userId: string,
  enabled: boolean
): Promise<{ ok: boolean; optimizedForOpenings: boolean }> =>
  adminFetch(`/users/${userId}/optimized-for-openings`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });

/**
 * Mark an account as running a windrower ahead of the harvester. Backend-side
 * this is a pure data flag — the external analytics scripts read it to decide
 * whether to divide down the yield on passes that carried several beds.
 */
export const updateUserOptimizedForWindrows = (
  userId: string,
  enabled: boolean
): Promise<{ ok: boolean; optimizedForWindrows: boolean }> =>
  adminFetch(`/users/${userId}/optimized-for-windrows`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });

/**
 * Start impersonating a user. Records an audit entry server-side and returns
 * the target's identity. The caller then opens the app with ?__impersonate=<id>.
 */
export const impersonateUser = (
  userId: string
): Promise<{
  ok: boolean;
  user: {
    id: string;
    email: string;
    name: string;
    subscriptionPaid: boolean;
    cameraInstalled: boolean;
  };
}> =>
  adminFetch(`/users/${userId}/impersonate`, { method: "POST" });

export const getAdminFields = (params?: {
  userId?: string;
  farmId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ fields: AdminField[]; total: number }> => {
  return api.get(`/api/admin/fields`, params);
};

export const getAdminSeasons = (params?: {
  userId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ seasons: AdminSeason[]; total: number }> => {
  return api.get(`/api/admin/seasons`, params);
};

export const getAdminFarms = (params?: {
  userId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ farms: AdminFarm[]; total: number }> => {
  return api.get(`/api/admin/farms`, params);
};

// ── Device Detail ──────────────────────────────────────────

export const getDeviceDetail = (esn: string): Promise<AdminDeviceDetail> =>
  adminFetch(`/devices/${esn}`);

export const updateDevice = (
  esn: string,
  data: {
    alias?: string;
    description?: string;
    model?: string;
    deviceType?: number;
    sensorMode?: number;
    sensorMin?: number;
    sensorMax?: number;
    width?: number;
    distance?: number;
    delay?: number;
    minSatellitesCount?: number;
    isStaticDevice?: boolean;
    fuelSensor?: boolean;
  }
): Promise<{ ok: boolean }> =>
  adminFetch(`/devices/${esn}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/**
 * Import historic track points from the legacy Go backend's CSV dump
 * (`stats-YYYY-MM-DD.csv` on DigitalOcean Spaces).
 *
 * This backend's own track history starts at the moment the device was pointed
 * here. This covers whatever the device did before that.
 *
 * One call = one chunk of CSV (header line + data lines), keeping each request
 * under the 10 MB admin body limit.
 */
export type ImportLegacyTracksResult = {
  ok: boolean;
  parse: {
    linesRead: number;
    skippedOtherDevice: number;
    skippedOutOfRange: number;
    skippedBadTime: number;
    skippedZeroGps: number;
    skippedMalformed: number;
  };
  candidateRows: number;
  skippedAlreadyPresent: number;
  deletedExisting: number;
  inserted: number;
  deviceTimeMin: string | null;
  deviceTimeMax: string | null;
  dryRun: boolean;
};

export const importLegacyTracks = (
  esn: string,
  body: {
    csv: string;
    from?: string;
    to?: string;
    dryRun?: boolean;
    replaceExisting?: boolean;
  }
): Promise<ImportLegacyTracksResult> =>
  adminFetch(`/devices/${esn}/import-legacy-tracks`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const getDeviceRequests = (
  esn: string
): Promise<{ requests: AdminDeviceRequest[] }> =>
  adminFetch(`/devices/${esn}/requests`);

export const sendDeviceRequest = (
  esn: string,
  data: Record<string, unknown>
): Promise<{ ok: boolean; requestId: number }> =>
  adminFetch(`/devices/${esn}/request`, {
    method: "POST",
    body: JSON.stringify({ data }),
  });

export const getCalibrations = (params?: {
  esn?: string;
  limit?: number;
  offset?: number;
}): Promise<{ calibrations: AdminCalibration[]; total: number }> => {
  return api.get(`/api/admin/calibrations`, params);
};

export const updateCalibrationCoefficient = (
  calibrationId: string,
  coefficient: number
): Promise<{ ok: boolean }> => {
  return api.put(`/api/admin/calibrations/${calibrationId}/coefficient`, {
    coefficient,
  });
};

export type CalibrationWorkflowStatus =
  | "pending"
  | "computing"
  | "completed"
  | "failed";

export const updateCalibrationStatus = (
  calibrationId: string,
  body: { status: CalibrationWorkflowStatus; error?: string | null }
): Promise<{ ok: boolean }> => {
  return api.patch(`/api/admin/calibrations/${calibrationId}`, body);
};

export const getTracks = (params: {
  esn: string;
  from: string;
  to: string;
  sample?: number;
}): Promise<{ esn: string; length: number; tracks: AdminTrackPoint[] }> => {
  return api.get(`/api/admin/tracks`, params);
};

export const getTracksGeometry = (params: {
  esn: string;
  from: string;
  to: string;
}): Promise<any> => {
  return api.get(`/api/admin/tracks/geometry`, params);
};

// ── Field Yield ──────────────────────────────────────────────

export type AdminYieldAggregate = {
  min: number;
  max: number;
  avg: number;
  totalMassKg: number | null;
  count: number;
  updatedAt: string;
};

/** Aggregates for many fields in one season — one request, no per-hex data. */
export const getFieldYieldAggregates = async (
  fieldIds: string[],
  seasonId: string
): Promise<Record<string, AdminYieldAggregate>> => {
  const res = await api.get<{ aggregates: Record<string, AdminYieldAggregate> }>(
    `/api/admin/yield-aggregates`,
    { seasonId, fieldIds: fieldIds.join(",") }
  );
  return res.aggregates ?? {};
};

export const uploadFieldYield = async (
  fieldId: string,
  seasonId: string,
  file: File
): Promise<{
  ok: boolean;
  imported: number;
  skipped: number;
  parseErrors?: string[];
  aggregate: { min: number; max: number; avg: number; totalMassKg: number; count: number };
}> => {
  return api.upload(`/api/admin/fields/${fieldId}/yield/upload`, file, "file", { seasonId });
};

export const deleteFieldYield = (
  fieldId: string,
  seasonId: string
): Promise<{ ok: boolean }> =>
  api.delete(`/api/admin/fields/${fieldId}/yield`, { seasonId });

// ── Analytics Settings ──────────────────────────────────────

export type AnalyticsBaseParams = {
  min_speed: number;
  max_speed: number;
  delay_s: number;
  delay_tolerance: number;
  lateral_side: "R" | "L";
  lateral_shift_m: number;
};

export type PotatoParams = {
  parameter: "sum" | "value" | "freq";
  baseline: number | "dynamic";
  baseline_value: number;
  baseline_freq: number;
  reaper: number;
  delta_min: number;
  delta_max: number;
};

export type PotatoParamsChange = PotatoParams & {
  start_date: string; // "YYYY-MM-DD HH:mm"
};

export type PotatoParamsConfig = {
  default: PotatoParams;
  changes: PotatoParamsChange[];
};

export type DefaultAnalyticsSettings = {
  retrofit: AnalyticsBaseParams;
  potato: AnalyticsBaseParams & {
    params_default: PotatoParams;
    /** The global potato time-based overrides, carried through a save untouched
     *  (the save used to send `changes: []` and wipe them). */
    params_changes?: PotatoParamsChange[];
  };
};

export const DEFAULT_ANALYTICS_SETTINGS: DefaultAnalyticsSettings = {
  retrofit: {
    min_speed: 1,
    max_speed: 15,
    delay_s: 16,
    delay_tolerance: 1,
    lateral_side: "R",
    lateral_shift_m: 0,
  },
  potato: {
    min_speed: 1,
    max_speed: 7,
    delay_s: 16,
    delay_tolerance: 1,
    lateral_side: "R",
    lateral_shift_m: 0,
    params_default: {
      parameter: "sum",
      baseline: 0,
      baseline_value: 0,
      baseline_freq: 0,
      reaper: -1,
      delta_min: 10,
      delta_max: 4000,
    },
  },
};

/** Merge partial API data with built-in defaults (empty `{}` from API must not skip defaults). */
export function mergePotatoParams(
  partial: Partial<PotatoParams> | null | undefined
): PotatoParams {
  return {
    ...DEFAULT_ANALYTICS_SETTINGS.potato.params_default,
    ...(partial && typeof partial === "object" ? partial : {}),
  };
}

/** Safe row for time-based overrides (null entries and missing fields). */
export function mergePotatoParamChange(
  ch: Partial<PotatoParamsChange> | Record<string, unknown> | null | undefined
): PotatoParamsChange {
  if (ch == null || typeof ch !== "object") {
    return { ...mergePotatoParams({}), start_date: "" };
  }
  const raw = ch as Record<string, unknown>;
  const start_date =
    typeof raw.start_date === "string" ? raw.start_date : "";
  const { start_date: _sd, ...rest } = raw;
  return {
    ...mergePotatoParams(rest as Partial<PotatoParams>),
    start_date,
  };
}

export const getDefaultAnalyticsSettings =
  (): Promise<DefaultAnalyticsSettings> =>
  adminFetch("/analytics/defaults").then((res: any) => {
    const retrofit =
      res.retrofit_can?.baseSettings ?? DEFAULT_ANALYTICS_SETTINGS.retrofit;
    const potato = res.potato;
    const potatoBase =
      potato?.baseSettings ?? {
        min_speed: DEFAULT_ANALYTICS_SETTINGS.potato.min_speed,
        max_speed: DEFAULT_ANALYTICS_SETTINGS.potato.max_speed,
        delay_s: DEFAULT_ANALYTICS_SETTINGS.potato.delay_s,
        delay_tolerance: DEFAULT_ANALYTICS_SETTINGS.potato.delay_tolerance,
        lateral_side: DEFAULT_ANALYTICS_SETTINGS.potato.lateral_side,
        lateral_shift_m: DEFAULT_ANALYTICS_SETTINGS.potato.lateral_shift_m,
      };
    const paramsDefault = mergePotatoParams(potato?.potatoParams?.default);
    const changes: unknown[] = Array.isArray(potato?.potatoParams?.changes)
      ? potato.potatoParams.changes
      : [];
    return {
      retrofit,
      potato: {
        ...potatoBase,
        params_default: paramsDefault,
        params_changes: changes
          .filter((c) => c != null && typeof c === "object")
          .map((c) => mergePotatoParamChange(c as Partial<PotatoParamsChange>)),
      },
    };
  });

export const updateDefaultAnalyticsSettings = (
  settings: DefaultAnalyticsSettings
): Promise<{ ok: boolean }> => {
  // The FULL base object as loaded (keys this form does not edit, like
  // min_flow_value / min_turn_radius_m, used to be dropped), and the loaded
  // `changes` instead of an empty list.
  const { params_default, params_changes, ...potatoBase } = settings.potato;
  return adminFetch("/analytics/defaults", {
    method: "PUT",
    body: JSON.stringify({
      retrofit_can: { baseSettings: settings.retrofit },
      potato: {
        baseSettings: potatoBase,
        potatoParams: {
          default: params_default,
          changes: params_changes ?? [],
        },
      },
    }),
  });
};

export type DeviceAnalyticsSettings = {
  retrofit?: AnalyticsBaseParams;
  potato?: AnalyticsBaseParams & {
    params?: PotatoParamsConfig;
    params_default?: PotatoParams;
  };
};

/** Same as {@link DeviceAnalyticsSettings} plus `deviceType` from the API (for UI that needs both). */
export type DeviceAnalyticsSettingsWithDeviceType = DeviceAnalyticsSettings & {
  deviceType: number;
};

/**
 * One line of a device's header-width history (admin view).
 *
 * Same timeline the device owner edits in the app — one list per ESN, not a
 * separate admin copy. Widths are metres. The earliest entry governs every
 * timestamp before the next one starts.
 */
export type AdminWidthSegment = {
  id: string;
  effectiveFrom: string;
  widthMeters: number;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
};

export const getDeviceWidthSegments = (
  esn: string
): Promise<AdminWidthSegment[]> =>
  adminFetch<{ segments: AdminWidthSegment[] }>(
    `/devices/${esn}/width-segments`
  ).then((res) => res.segments ?? []);

/** Replaces the whole list — validity is a property of the set, so there is no per-row edit. */
export const setDeviceWidthSegments = (
  esn: string,
  segments: Array<{ effectiveFrom: string; widthMeters: number; note?: string | null }>
): Promise<AdminWidthSegment[]> =>
  adminFetch<{ segments: AdminWidthSegment[] }>(`/devices/${esn}/width-segments`, {
    method: "PUT",
    body: JSON.stringify({ segments }),
  }).then((res) => res.segments ?? []);

export const getDeviceAnalyticsSettings = (
  esn: string
): Promise<DeviceAnalyticsSettingsWithDeviceType> =>
  adminFetch(`/devices/${esn}/analytics`).then((res: any) => {
    const deviceType = res.deviceType ?? 1;
    const isRetrofitOrCan = deviceType === 1 || deviceType === 2;
    const base = res.baseSettings ?? {};

    if (isRetrofitOrCan) {
      return { retrofit: base, deviceType };
    }
    const raw = res.potatoParams as PotatoParamsConfig | null | undefined;
    const params: PotatoParamsConfig | undefined = raw
      ? {
          default: mergePotatoParams(raw.default),
          changes: Array.isArray(raw.changes)
            ? (raw.changes as unknown[])
                .filter((c) => c != null && typeof c === "object")
                .map((c) => mergePotatoParamChange(c as Partial<PotatoParamsChange>))
            : [],
        }
      : undefined;
    return {
      potato: {
        ...base,
        params,
        params_default: DEFAULT_ANALYTICS_SETTINGS.potato.params_default,
      },
      deviceType,
    };
  });

// ── Common analytics parameters (processing thresholds, hex config) ──
// The backend calibration compute reads `potato` / `grain.hex_n_points` (at
// resolution 13); the other values are not used by any algorithm yet.

export type HexParam = {
  resolution: number;
  surface: number;
  kring: number;
  n_points: number;
  n_agg_points: number;
};

export type CommonAnalyticsParams = {
  speedLimit: number;
  yieldCoeff: number;
  overwriteThresh: number;
  areaThresh: number;
  minCount: number;
  hex: { "12": HexParam; "13": HexParam; "14": HexParam };
  potato: { hex_n_points: Record<string, number> };
  grain: { hex_n_points: Record<string, number>; hex_n_agg_points: Record<string, number> };
};

export const DEFAULT_COMMON_ANALYTICS_PARAMS: CommonAnalyticsParams = {
  speedLimit: 20,
  yieldCoeff: 0.01,
  overwriteThresh: 0.05,
  areaThresh: 50,
  minCount: 20,
  hex: {
    "12": { resolution: 12, surface: 0.03071, kring: 1, n_points: 9, n_agg_points: 8 },
    "13": { resolution: 13, surface: 0.00439, kring: 1, n_points: 3, n_agg_points: 6 },
    "14": { resolution: 14, surface: 0.0006267, kring: 1, n_points: 4, n_agg_points: 5 },
  },
  potato: { hex_n_points: { "12": 30, "13": 15, "14": 3 } },
  grain: {
    hex_n_points: { "12": 12, "13": 3, "14": 4 },
    hex_n_agg_points: { "12": 12, "13": 3, "14": 4 },
  },
};

export const getCommonAnalyticsParams = (): Promise<{
  params: CommonAnalyticsParams;
  updatedAt: string | null;
}> =>
  adminFetch("/analytics/common-params").then((res: any) => ({
    params: res.params ?? DEFAULT_COMMON_ANALYTICS_PARAMS,
    updatedAt: res.updatedAt ?? null,
  }));

export const updateCommonAnalyticsParams = (
  params: CommonAnalyticsParams
): Promise<{ ok: boolean }> =>
  adminFetch("/analytics/common-params", {
    method: "PUT",
    body: JSON.stringify({ params }),
  });

export const updateDeviceAnalyticsSettings = (
  esn: string,
  data: DeviceAnalyticsSettings
): Promise<{ ok: boolean }> => {
  const body: { baseSettings?: AnalyticsBaseParams; potatoParams?: PotatoParamsConfig } = {};
  if (data.retrofit) {
    body.baseSettings = data.retrofit;
  }
  if (data.potato) {
    // The full base object as loaded, not six hand-picked keys.
    const { params, params_default: _paramsDefault, ...base } = data.potato;
    body.baseSettings = base;
    if (params) {
      body.potatoParams = params;
    }
  }
  return adminFetch(`/devices/${esn}/analytics`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
};

export const detachDevice = (attachmentId: string): Promise<{ ok: boolean }> =>
  adminFetch(`/attachments/${attachmentId}/detach`, { method: "POST" });

export const attachUserDevice = (
  userId: string,
  params: { esn: string; name: string; model: string; width: number }
): Promise<{ device: { id: string; esn: string } }> =>
  adminFetch(`/users/${userId}/devices`, {
    method: "POST",
    body: JSON.stringify(params),
  });

// ── Customer Notification Banners (multiple; `enabled` = published) ──

export type AdminBanner = {
  id: number;
  message: string;
  severity: BannerSeverity;
  enabled: boolean;
  updatedAt: string | null;
  createdAt: string | null;
};

export const getNotificationBanners = (): Promise<{ banners: AdminBanner[] }> =>
  adminFetch("/notification-banner");

export const createNotificationBanner = (): Promise<{ banner: AdminBanner }> =>
  adminFetch("/notification-banner", {
    method: "POST",
    body: JSON.stringify({}),
  });

export const updateNotificationBanner = (
  id: number,
  data: { message: string; severity: BannerSeverity; enabled: boolean }
): Promise<{ banner: AdminBanner }> =>
  adminFetch(`/notification-banner/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deleteNotificationBanner = (id: number): Promise<{ ok: boolean }> =>
  adminFetch(`/notification-banner/${id}`, {
    method: "DELETE",
  });

// ── Configs (feature-flag gating) ──────────────────────────────────

export type AdminConfig = {
  id: string;
  key: string;
  name: string;
  enabledForAll: boolean;
  createdAt: string;
  userIds: string[];
};

export const getConfigs = (): Promise<{ configs: AdminConfig[] }> =>
  adminFetch("/configs");

export const createConfig = (body: {
  key: string;
  name: string;
  enabledForAll?: boolean;
  userIds?: string[];
}): Promise<{ id: string }> =>
  adminFetch("/configs", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateConfig = (
  id: string,
  body: { name?: string; enabledForAll?: boolean; userIds?: string[] }
): Promise<{ ok: boolean }> =>
  adminFetch(`/configs/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deleteConfig = (id: string): Promise<{ ok: boolean }> =>
  adminFetch(`/configs/${id}`, {
    method: "DELETE",
  });

// ── Hologram SIM ─────────────────────────────────────────────

export type AdminSimOverview = {
  deviceId: number;
  simcardId: number;
  linkId: number;
  name: string;
  /** Hologram link state: LIVE, PAUSED-USER, TEST-ACTIVATE, … */
  state: string;
  carrier: number | null;
  apn: string | null;
  msisdn: string | null;
  imei: string | null;
  openSession: boolean;
  usageBytes: number | null;
  billingPeriodEnd: string | null;
};

export type AdminSimAction = {
  id: string;
  action: "refresh" | "pause" | "resume" | "sms";
  /** `{ body }` for sms, null otherwise. */
  payload: { body?: string } | null;
  ok: boolean;
  error: string | null;
  adminEmail: string;
  createdAt: string;
};

export type AdminSimSession = {
  sessionBegin: string | null;
  sessionEnd: string | null;
  bytes: number;
  networkName: string | null;
  cellId: string | null;
  radioAccessTechnology: string | null;
  imei: string | null;
};

/** Silent: the card renders "no Hologram SIM" itself instead of a toast. */
export const getDeviceSim = (
  esn: string
): Promise<{ iccid: string; sim: AdminSimOverview; actions: AdminSimAction[] }> =>
  api.get(`/api/admin/devices/${esn}/sim`, undefined, { silent: true });

export const getDeviceSimSessions = (
  esn: string,
  limit = 20
): Promise<{ sessions: AdminSimSession[] }> =>
  adminFetch(`/devices/${esn}/sim/sessions?limit=${limit}`);

export const refreshDeviceSim = (esn: string): Promise<{ ok: boolean }> =>
  adminFetch(`/devices/${esn}/sim/refresh`, { method: "POST", body: "{}" });

export const setDeviceSimState = (
  esn: string,
  state: "pause" | "live"
): Promise<{ ok: boolean }> =>
  adminFetch(`/devices/${esn}/sim/state`, {
    method: "POST",
    body: JSON.stringify({ state }),
  });

export const sendDeviceSimSms = (esn: string, body: string): Promise<{ ok: boolean }> =>
  adminFetch(`/devices/${esn}/sim/sms`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
